import React, { forwardRef, useEffect } from 'react';
import { Search, X, Calendar, ChevronDown } from 'lucide-react';

// Filteri za Zadatke i Intervencije:
// - SearchField: polje za pretragu, otvara se povećalom u navbaru (App.js) i ostaje otvoreno dok ima teksta.
//   Uvijek je u DOM-u (samo stisnuto) da ga dodir na povećalo može fokusirati u istoj gesti — inače iOS ne pokaže tipkovnicu.
// - ActiveStrip: tanka traka uključenih filtera s × i brojem rezultata; nema je kad ništa nije uključeno.
// - Panel na FAB (FilterSheet): brzi čipovi s brojem, vrijeme, pa „Napredni filteri” koji se rasklapaju u istom panelu.

const DAY = 86400000;
const sod = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const pad = (n) => String(n).padStart(2, '0');
export const toDateInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromDateInput = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };

// Zadaci gledaju naprijed, intervencije unatrag
export const TIME_FUTURE = [
  { k: 'today', l: 'Danas' },
  { k: '7d', l: 'Sljedećih 7 dana', chip: '7 dana' },
  { k: '30d', l: 'Sljedećih 30 dana', chip: '30 dana' },
  { k: 'month', l: 'Ovaj mjesec' },
  { k: 'last', l: 'Prošli mjesec' },
];
export const TIME_PAST = [
  { k: '7d', l: 'Zadnjih 7 dana', chip: '7 dana' },
  { k: '30d', l: 'Zadnjih 30 dana', chip: '30 dana' },
  { k: 'month', l: 'Ovaj mjesec' },
  { k: 'last', l: 'Prošli mjesec' },
];

// { from, to } (to isključivo) ili null za „Bilo kada” (tada vrijedi godina iz Arhive)
export function timeRange(time, past, custom) {
  const t0 = sod(new Date());
  switch (time) {
    case 'today': return { from: t0, to: new Date(t0.getTime() + DAY) };
    case '7d': return past ? { from: new Date(t0.getTime() - 6 * DAY), to: new Date(t0.getTime() + DAY) } : { from: t0, to: new Date(t0.getTime() + 7 * DAY) };
    case '30d': return past ? { from: new Date(t0.getTime() - 29 * DAY), to: new Date(t0.getTime() + DAY) } : { from: t0, to: new Date(t0.getTime() + 30 * DAY) };
    case 'month': return { from: new Date(t0.getFullYear(), t0.getMonth(), 1), to: new Date(t0.getFullYear(), t0.getMonth() + 1, 1) };
    case 'last': return { from: new Date(t0.getFullYear(), t0.getMonth() - 1, 1), to: new Date(t0.getFullYear(), t0.getMonth(), 1) };
    case 'custom': {
      if (!custom?.from || !custom?.to) return null;
      const a = fromDateInput(custom.from), b = fromDateInput(custom.to);
      const [lo, hi] = a <= b ? [a, b] : [b, a];
      return { from: lo, to: new Date(hi.getFullYear(), hi.getMonth(), hi.getDate() + 1) };
    }
    default: return null;
  }
}

// Godine koje treba učitati (podaci su keširani po godini); najviše 3 unatrag od kraja raspona
export function rangeYears(range, year) {
  if (!range) return [year];
  const a = range.from.getFullYear(), b = new Date(range.to.getTime() - 1).getFullYear();
  const out = [];
  for (let y = Math.max(a, b - 2); y <= b; y++) out.push(y);
  return out;
}

export function timeLabel(time, list, custom, year, currentYear) {
  if (time === 'custom') {
    const r = timeRange('custom', false, custom);
    if (!r) return 'Razdoblje';
    const b = new Date(r.to.getTime() - DAY);
    return `${r.from.getDate()}. ${r.from.getMonth() + 1}. – ${b.getDate()}. ${b.getMonth() + 1}.`;
  }
  const o = list.find(x => x.k === time);
  if (o) return o.chip || o.l;
  return year !== currentYear ? `Godina ${year}` : 'Vrijeme';
}

export const norm = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const defaultCustom = () => {
  const t = new Date();
  return { from: toDateInput(new Date(t.getFullYear(), t.getMonth(), 1)), to: toDateInput(new Date(t.getFullYear(), t.getMonth() + 1, 0)) };
};

const plural = (n, noun) => (n % 10 === 1 && n % 100 !== 11 ? noun[0] : (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) ? noun[1] : noun[2]);

// ── Stranica ──

export const SearchField = forwardRef(function SearchField({ open, query, setQuery, onClose, placeholder }, ref) {
  const shown = open || !!query;
  // Skrolanje popisa spušta tipkovnicu (pretraga ostaje). Gleda se pomak prsta, ne scroll događaj —
  // iOS sam skrola stranicu kad se tipkovnica otvara, pa bi je scroll odmah zatvorio.
  useEffect(() => {
    let y0 = null;
    const onStart = (e) => { y0 = e.touches[0]?.clientY ?? null; };
    const onMove = (e) => {
      const el = ref && ref.current;
      if (!el || document.activeElement !== el || y0 == null) return;
      if (el.closest('.fb-search')?.contains(e.target)) return;
      if (Math.abs((e.touches[0]?.clientY ?? y0) - y0) > 12) el.blur();
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    return () => { window.removeEventListener('touchstart', onStart); window.removeEventListener('touchmove', onMove); };
  }, [ref]);
  return (
    <div className={`fb-searchwrap${shown ? ' open' : ''}`} aria-hidden={!shown}>
      <label className="fb-search">
        <Search size={18} />
        <input ref={ref} type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={placeholder}
          autoComplete="off" enterKeyHint="search" tabIndex={shown ? 0 : -1}
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') onClose(); }}
          onBlur={() => { if (!query) onClose(); }} />
        <button type="button" aria-label="Zatvori pretragu" tabIndex={shown ? 0 : -1} onMouseDown={e => e.preventDefault()} onClick={onClose}><X size={17} /></button>
      </label>
    </div>
  );
});

// Tekst pretrage se ne ponavlja kao čip (vidi se u polju iznad) — traka tada pokazuje samo broj
export function ActiveStrip({ items, count, noun, onReset }) {
  if (!items.length) return null;
  const chips = items.filter(a => a.key !== 'q');
  return (
    <div className="fb-strip">
      {chips.length > 0 && <div className="fb-chips">
        {chips.map(a => (
          <button key={a.key} type="button" className="fb-chip applied" onClick={a.onRemove} title="Makni filter">
            {a.label}<X size={14} />
          </button>
        ))}
      </div>}
      <div className="fb-sum">
        <span><b>{count}</b> {plural(count, noun)}</span>
        <button type="button" onClick={onReset}>Očisti sve</button>
      </div>
    </div>
  );
}

// Prazno stanje kad filteri ne daju ništa: kaže zbog čega i nudi da se maknu
export function EmptyFiltered({ noun, reasons, onReset }) {
  return (
    <div className="fb-empty">
      <b>Nema {noun}</b>
      {reasons.length > 0 && <div>za: {reasons.join(' · ')}</div>}
      {reasons.length > 0 && <button type="button" onClick={onReset}>Makni filtere</button>}
    </div>
  );
}

// ── Panel (FilterSheet) ──

export function QuickChip({ icon: Icon, label, on, count, onClick }) {
  return (
    <button type="button" className={`fb-chip${on ? ' on' : ''}`} onClick={onClick}>
      {Icon && <Icon size={15} />}{label}{count != null && <span className="fb-n">{count}</span>}
    </button>
  );
}

export function SheetLabel({ children }) {
  return <div className="fb-lbl">{children}</div>;
}

// Vrijeme: izbor + vlastito razdoblje
export function TimeQuick({ list, time, setTime, custom, setCustom }) {
  return (
    <>
      <div className="fb-wrap">
        <button type="button" className={`fb-chip${time === 'all' ? ' on' : ''}`} onClick={() => setTime('all')}>Bilo kada</button>
        {list.map(o => (
          <button key={o.k} type="button" className={`fb-chip${time === o.k ? ' on' : ''}`} onClick={() => setTime(o.k)}>{o.l}</button>
        ))}
        <button type="button" className={`fb-chip${time === 'custom' ? ' on' : ''}`} onClick={() => setTime('custom')}><Calendar size={15} /> Razdoblje…</button>
      </div>
      {time === 'custom' && (
        <div className="fb-range">
          <label>Od<input type="date" value={custom.from} onChange={e => e.target.value && setCustom(c => ({ ...c, from: e.target.value }))} /></label>
          <label>Do<input type="date" value={custom.to} onChange={e => e.target.value && setCustom(c => ({ ...c, to: e.target.value }))} /></label>
        </div>
      )}
    </>
  );
}

export function ArchiveRow({ year, setYear, setTime, years }) {
  return (
    <div className="fb-archive">
      <select value={year} onChange={e => { setYear(parseInt(e.target.value, 10)); setTime('all'); }}>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <span className="fb-hint">godina kad je vrijeme „Bilo kada”</span>
    </div>
  );
}

// Redak s prekidačem
export function OptRow({ title, sub, on, onClick }) {
  return (
    <button type="button" className={`fb-opt${on ? ' on' : ''}`} onClick={onClick}>
      <span className="fb-opt-main"><span className="fb-opt-t">{title}</span>{sub && <span className="fb-opt-s">{sub}</span>}</span>
      <span className={`tf-sw${on ? ' on' : ''}`} aria-hidden="true" />
    </button>
  );
}

// „Napredni filteri” — rasklapa se u istom panelu
export function AdvancedToggle({ open, onToggle, activeCount, children }) {
  return (
    <>
      <button type="button" className={`fb-adv${open ? ' open' : ''}`} onClick={onToggle} aria-expanded={open}>
        <span>Napredni filteri</span>
        {activeCount > 0 && <span className="fb-dot">{activeCount}</span>}
        <ChevronDown size={18} style={{ marginLeft: 'auto', transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && <div className="fb-advbody">{children}</div>}
    </>
  );
}
